import type { PickingInfo } from '@deck.gl/core';
import type {
  FerryProps,
  StationProps,
  FlareProps,
  CameraProps,
  LinknycProps,
  FlightProps,
  FloodSensorProps,
  EneOutageProps,
  AisVesselProps,
  PowerOutageProps,
  HarborStationProps,
} from '@vitals/shared';
import { floodBand } from '../layers/floodnetLayer.js';
import type { CableDatum, CableTerminalDatum } from '../layers/cablesLayer.js';

function ageText(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

/** ConEd ETR: ISO string → "restore by 10:30 PM"; "ETR-EXP" / null handled. */
function etrText(etr: string | null): string {
  if (etr === 'ETR-EXP') return 'restoration overdue';
  if (!etr) return 'restoration time unknown';
  const d = new Date(etr);
  if (Number.isNaN(d.getTime())) return 'restoration time unknown';
  return `restore by ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

/** "213 UNION AVENUE" → "213 Union Avenue" (kiosk addresses arrive uppercase). */
function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ESC[c]!);
}

interface Tip {
  html: string;
  style: Record<string, string>;
}

/** Feed-safe presentation shared by transient hover and persistent selection. */
export interface MapPickPresentation {
  title: string;
  subtitle?: string;
}

/** A two-line tooltip card. Title + optional muted subtitle; both escaped. */
function card(title: string, subtitle?: string): MapPickPresentation {
  return { title, subtitle };
}

export function countdownText(arrivalAt: unknown, nowMs: unknown): string | undefined {
  if (typeof arrivalAt !== 'number' || !Number.isFinite(arrivalAt)
    || typeof nowMs !== 'number' || !Number.isFinite(nowMs) || arrivalAt <= nowMs) return undefined;
  const remaining = arrivalAt - nowMs;
  return remaining < 60_000 ? 'Due' : `${Math.floor(remaining / 60_000)} min`;
}

/** Normalize a picked map object into a stable, DOM-independent presentation. */
export function describeMapPick(
  info: PickingInfo,
): MapPickPresentation | null {
  const o = info.object as
    | ({
        label?: string;
        destination?: string;
        nextStop?: string;
        nextStopAt?: number;
        nowMs?: number;
        vehicleId?: string;
      } & {
        props?: Record<string, unknown>;
        properties?: Record<string, unknown>;
      })
    | null;
  if (!o) return null;

  switch (info.layer?.id) {
    case 'subwayStations': {
      // Station markers use the richer persistent-hover card in stationHover.ts.
      return null;
    }
    case 'subwayStationExtents': {
      const extent = o as unknown as { name?: string };
      return typeof extent.name === 'string' ? card(extent.name, 'Station extent') : null;
    }
    case 'subwayStationEntrances': {
      const entrance = o as unknown as {
        name?: string;
        type?: string;
        entryAllowed?: boolean;
        exitAllowed?: boolean;
      };
      if (typeof entrance.name !== 'string' || typeof entrance.type !== 'string') return null;
      const direction = entrance.entryAllowed && entrance.exitAllowed ? 'entry and exit'
        : entrance.entryAllowed ? 'entry only' : entrance.exitAllowed ? 'exit only' : 'no entry or exit';
      const type = entrance.type.replace('-', ' ').replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
      return card(entrance.name, `${type} · ${direction}`);
    }
    case 'subway': {
      const parts: string[] = [];
      if (o.destination) parts.push(`to ${o.destination}`);
      if (o.nextStop) parts.push(`next ${o.nextStop}`);
      const countdown = countdownText(o.nextStopAt, o.nowMs);
      if (countdown) parts.push(countdown);
      return card(`${o.label ?? 'Subway'} train`, parts.join(' · ') || undefined);
    }
    case 'lirrLines': {
      const branch = o.properties?.branch;
      return typeof branch === 'string' ? card(branch) : null;
    }
    case 'lirrStations': {
      // Station data are raw GeoJSON features: name lives under `properties`.
      const name = o.properties?.name;
      return typeof name === 'string' ? card(name) : null;
    }
    case 'mnrLines': {
      const branch = o.properties?.branch;
      return typeof branch === 'string' ? card(branch) : null;
    }
    case 'mnrStations': {
      // Station data are raw GeoJSON features: name lives under `properties`.
      const name = o.properties?.name;
      return typeof name === 'string' ? card(name) : null;
    }
    case 'pathLines': {
      const branch = o.properties?.branch;
      return typeof branch === 'string' ? card(branch) : null;
    }
    case 'pathStations': {
      // Station data are raw GeoJSON features: name lives under `properties`.
      const name = o.properties?.name;
      return typeof name === 'string' ? card(name) : null;
    }
    case 'lirr': {
      const parts: string[] = [];
      if (o.destination) parts.push(`to ${o.destination}`);
      if (o.nextStop) parts.push(`next ${o.nextStop}`);
      return card('LIRR train', parts.join(' · ') || undefined);
    }
    case 'mnr': {
      const parts: string[] = [];
      if (o.destination) parts.push(`to ${o.destination}`);
      if (o.nextStop) parts.push(`next ${o.nextStop}`);
      return card('Metro-North train', parts.join(' · ') || undefined);
    }
    case 'path': {
      const parts: string[] = [];
      if (o.destination) parts.push(`to ${o.destination}`);
      if (o.nextStop) parts.push(`next ${o.nextStop}`);
      return card('PATH train', parts.join(' · ') || undefined);
    }
    case 'ferry': {
      const p = (o.props ?? {}) as unknown as FerryProps;
      const parts: string[] = [];
      if (p.routeName) parts.push(p.routeName);
      if (p.nextStop) parts.push(`next ${p.nextStop}`);
      return card(p.label ? `Ferry ${p.label}` : 'NYC Ferry', parts.join(' · ') || undefined);
    }
    case 'citibike': {
      const p = (o.props ?? {}) as unknown as StationProps;
      return card(p.name, `${p.bikes} bikes · ${p.docks} docks`);
    }
    case 'c311': {
      const p = (o.props ?? {}) as unknown as FlareProps;
      return card(p.kind, ageText(p.ageSec ?? 0));
    }
    case 'buses': {
      // The buses layer pushes flat pick datums rather than a `props` bag.
      const d = o as unknown as {
        line?: string;
        vehicleId?: string;
        destination?: string;
        nextStop?: string;
      };
      const fleetNumber = cleanBusFleetNumber(d.vehicleId);
      const title = `${d.line ? `${d.line} bus` : 'Bus'}${fleetNumber ? ` · #${fleetNumber}` : ''}`;
      const parts: string[] = [];
      if (d.destination) parts.push(`to ${titleCaseIfUppercase(d.destination)}`);
      if (d.nextStop) parts.push(`next ${titleCaseIfUppercase(d.nextStop)}`);
      return card(title, parts.join(' · ') || undefined);
    }
    case 'cams': {
      const p = (o.props ?? {}) as unknown as CameraProps;
      return card(p.name, p.roadway ? `${p.roadway} · click to view` : 'click to view');
    }
    case 'linknyc': {
      const p = (o.props ?? {}) as unknown as LinknycProps;
      // type like "Link5G_Ad"/"Link5G_NonAd" → "Link5G"; "Link1.0" stays.
      const kind = p.type?.replace(/_.*$/, '') ?? 'LinkNYC';
      const parts = [kind, p.boro].filter(Boolean);
      if (p.status && p.status !== 'Live') parts.push(p.status);
      return card(titleCase(p.address) || 'LinkNYC kiosk', parts.join(' · ') || undefined);
    }
    case 'flights': {
      const p = (o.props ?? {}) as unknown as FlightProps;
      const title = p.flightNumber || p.callsign || p.registration || 'Aircraft';
      const parts: string[] = [];
      if (p.airline) parts.push(p.airline);
      if (p.isHelicopter) parts.push('helicopter');
      if (p.typeCode) parts.push(p.typeCode);
      if (typeof p.altitude === 'number') parts.push(`${p.altitude.toLocaleString()} ft`);
      if (p.origin && p.destination) parts.push(`${p.origin} → ${p.destination}`);
      return card(title, parts.join(' · ') || undefined);
    }
    case 'floodnet': {
      const p = (o.props ?? {}) as unknown as FloodSensorProps;
      const depth = typeof p.depthIn === 'number' ? p.depthIn : 0;
      const { band } = floodBand(depth);
      return card(p.name || 'Flood sensor', `${Math.round(depth)}″ · ${band}`);
    }
    case 'ene': {
      const p = (o.props ?? {}) as unknown as EneOutageProps;
      const title = `${p.kind === 'elevator' ? 'Elevator' : 'Escalator'} · ${p.station}`;
      const parts = [p.serving, p.reason].filter(Boolean);
      if (p.ada) parts.push('ADA');
      return card(title, parts.join(' · ') || undefined);
    }
    case 'ais': {
      const p = (o.props ?? {}) as unknown as AisVesselProps;
      const parts = [p.category, `${Math.round(p.sog)} kn`];
      if (p.status && p.status !== 'under way') parts.push(p.status);
      return card(p.name || 'Vessel', parts.join(' · '));
    }
    case 'harbor': {
      const p = (o.props ?? {}) as unknown as HarborStationProps;
      const parts: string[] = [];
      if (p.waterTempF !== undefined) parts.push(`${p.waterTempF}°F`);
      if (p.waveHeightFt !== undefined) parts.push(`${p.waveHeightFt} ft`);
      if (p.windMph !== undefined) {
        const wind = p.windDir ? `${Math.round(p.windMph)} mph ${p.windDir}` : `${Math.round(p.windMph)} mph`;
        parts.push(wind);
      }
      if (p.airTempF !== undefined) parts.push(`air ${p.airTempF}°F`);
      return card(p.name || 'Buoy', parts.join(' · ') || undefined);
    }
    case 'power_outages': {
      const p = (o.props ?? {}) as unknown as PowerOutageProps;
      const out = typeof p.customersOut === 'number' ? p.customersOut : 0;
      const title = out === 1 ? '1 customer without power' : `${out.toLocaleString()} without power`;
      const incidents = typeof p.incidents === 'number' ? p.incidents : 0;
      const parts = [`${incidents} outage${incidents === 1 ? '' : 's'}`, etrText(p.etr ?? null)];
      return card(title, parts.join(' · '));
    }
    case 'grid_load': {
      const d = o as unknown as CableDatum;
      const mwText =
        d.flow != null
          ? `${Math.round(Math.abs(d.flow.mw)).toLocaleString()} / ${d.cable.capacityMw.toLocaleString()} MW`
          : `— / ${d.cable.capacityMw.toLocaleString()} MW`;
      return card(`${d.cable.shortName} power line`, `${d.cable.origin} → ${d.cable.landing} · ${mwText}`);
    }
    case 'grid-cable-terminals': {
      const d = o as unknown as CableTerminalDatum;
      return card(d.terminusName, `${d.cable.shortName} power line terminus`);
    }
    case 'location': {
      const d = o as unknown as { accuracyMeters?: number };
      const acc = typeof d.accuracyMeters === 'number'
        ? `±${Math.round(d.accuracyMeters)}m`
        : undefined;
      return card('Your location', acc);
    }
    default:
      return null;
  }
}

/** Remove the agency portion of identifiers such as `MTA NYCT_9771`. */
export function cleanBusFleetNumber(vehicleId: unknown): string | undefined {
  if (typeof vehicleId !== 'string') return undefined;
  const cleaned = vehicleId.trim().replace(/^.*_/, '').trim();
  return cleaned || undefined;
}

/** Normalize feed strings that are entirely uppercase without damaging mixed-case names. */
function titleCaseIfUppercase(value: string): string {
  return value === value.toUpperCase() ? titleCase(value) : value;
}

/** Hover tooltip for a picked map item. Persistent selection uses the same description. */
export function getMapTooltip(info: PickingInfo): Tip | null {
  const presentation = describeMapPick(info);
  if (!presentation) return null;
  const subHtml = presentation.subtitle
    ? `<div style="opacity:.7;font-size:11px;margin-top:1px">${esc(presentation.subtitle)}</div>`
    : '';
  return {
    html: `<div style="font-weight:600">${esc(presentation.title)}</div>${subHtml}`,
    style: {
      background: 'rgba(8,12,20,0.92)',
      color: '#e8eefc',
      padding: '6px 9px',
      borderRadius: '8px',
      fontSize: '13px',
      lineHeight: '1.25',
      boxShadow: '0 4px 16px rgba(0,0,0,.5)',
    },
  };
}
